import React, { useRef, useCallback } from 'react';
import { SplitButtonGroup } from './SplitButtonGroup';
import Button from './Button';
import { Modal, ModalBody, ModalFooter, ModalHeader } from './modal';

export interface BasicModalProps {
  isOpen: boolean;
  headerText: string;
  bodyText: string | (() => string);
  onCancel?: () => void;
  onConfirm: () => void;
  onDiscard?: () => void;
  onModalDisable?: () => void;
  cancelButtonText?: string;
  confirmButtonText?: string;
  discardButtonText?: string;
  isConfirmDanger?: boolean;
  children?: React.ReactNode;
  'data-testid'?: string;
}

/**
 * A basic modal dialog with two buttons: cancel / confirm.
 *
 * @param isOpen indicates if the modal dialog is open
 * @param headerText text displayed in the modal header
 * @param bodyText text displayed in the modal body
 * @param onCancel callback for the cancel button; if not provided, button not shown
 * @param onConfirm callback for the confirm button
 * @param onDiscard callback for the discard button; if not provided, button not shown
 * @param cancelButtonText optional text for the cancel button, defaults to 'Cancel'
 * @param confirmButtonText optional text for the confirm button, defaults to 'Okay'
 * @param discardButtonText optional text for the discard button, defaults to 'Discard'
 */
function BasicModal(props: BasicModalProps): JSX.Element {
  const {
    isOpen,
    headerText,
    bodyText,
    onCancel,
    onConfirm,
    onDiscard,
    onModalDisable,
    cancelButtonText = 'Cancel',
    confirmButtonText = 'Okay',
    discardButtonText = 'Discard',
    isConfirmDanger = false,
    children,
    'data-testid': dataTestId,
  } = props;

  const confirmButton = useRef<HTMLButtonElement>(null);

  const disableModalCheckbox = useRef<HTMLInputElement>(null);

  const onConfirmClicked = useCallback(() => {
    if (
      disableModalCheckbox.current !== null &&
      disableModalCheckbox.current.checked &&
      onModalDisable
    ) {
      onModalDisable();
    }
    onConfirm();
  }, [onConfirm, onModalDisable]);

  const onOpened = useCallback(() => {
    confirmButton.current?.focus();
  }, []);

  let modalBody = '';
  if (isOpen) {
    modalBody = typeof bodyText === 'function' ? bodyText() : bodyText;
  }

  return (
    <Modal isOpen={isOpen} onOpened={onOpened}>
      <ModalHeader closeButton={false}>{headerText}</ModalHeader>
      <ModalBody>{modalBody}</ModalBody>
      <ModalFooter>
        {onModalDisable && (
          <div className="custom-control custom-checkbox form-group mr-auto">
            <input
              type="checkbox"
              className="custom-control-input"
              id="move-confirmation-checkbox"
              defaultChecked={false}
              ref={disableModalCheckbox}
              data-testid={
                dataTestId !== undefined
                  ? `${dataTestId}-checkbox-confirm`
                  : undefined
              }
            />
            <label
              className="custom-control-label"
              htmlFor="move-confirmation-checkbox"
            >
              Don&#39;t ask me again
            </label>
          </div>
        )}
        {onDiscard && (
          <Button
            kind="secondary"
            className="mr-auto"
            data-dismiss="modal"
            onClick={onDiscard}
            data-testid={
              dataTestId !== undefined ? `${dataTestId}-btn-discard` : undefined
            }
          >
            {discardButtonText}
          </Button>
        )}
        {onCancel && (
          <Button
            kind="secondary"
            data-dismiss="modal"
            onClick={onCancel}
            data-testid={
              dataTestId !== undefined ? `${dataTestId}-btn-cancel` : undefined
            }
          >
            {cancelButtonText}
          </Button>
        )}
        <SplitButtonGroup>
          <Button
            kind={isConfirmDanger ? 'danger' : 'primary'}
            onClick={onConfirmClicked}
            ref={confirmButton}
            data-testid={
              dataTestId !== undefined ? `${dataTestId}-btn-confirm` : undefined
            }
          >
            {confirmButtonText}
          </Button>
          {children}
        </SplitButtonGroup>
      </ModalFooter>
    </Modal>
  );
}

export default BasicModal;
